import { KoobButton, KoobModal, KoobPlaceholder } from '@koob/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import KoobTitle from '../../../../components/Koob/KoobTitle';
import { formatDate } from '@koob/dates';
import { KoobInputSelect } from '@koob/ui';
import { useField } from 'formik';
import KoobParagraph from '../../../../components/Koob/KoobParagraph';
import { useAsync } from 'react-async';
import { getHotelContracts } from 'api/node/contracts';
import { ContractStateFilterEnum, HotelContractStatusEnum } from '@koob/enums';

function ContractListItem({ itemField, contract }) {
  const { t } = useTranslation('toConnectionRequest');

  const [{ value: field }, , { setValue: setField }] = useField(
    `${itemField}.contracts`,
  );

  const findIndex = field?.findIndex(
    (fieldItem) => String(fieldItem.id) === String(contract.id),
  );
  const contractIndex = findIndex >= 0 ? findIndex : null;

  const fieldName = `${itemField}.contracts[${contractIndex}].enabled`;
  const [{ value: enabledValue }, , { setValue: setEnabledValue }] =
    useField(fieldName);
  const startAt = contract?.startAt;
  const endAt = contract?.endAt;

  const addItem = () => {
    if (contractIndex !== null) {
      return;
    }

    setField([
      ...(field ?? []),
      {
        id: contract.id,
        enabled: null,
        startAt,
        endAt,
      },
    ]);
  };

  return (
    <div className="border p-5 rounded-md">
      <div className="flex justify-between items-center">
        <div>
          <div className="font-medium text-lg text-gray-800">
            {formatDate(startAt, 'DD/MM/YYYY')} to{' '}
            {formatDate(endAt, 'DD/MM/YYYY')}
          </div>

          <div className="uppercase italic font-medium text-green-500">
            {contract?.status}
          </div>
        </div>

        <div>
          {contractIndex === null ? (
            <KoobButton variant="primary" onClick={() => addItem()}>
              <i className="far fa-eye mr-1" />
              {t('bulkEdit.editor.contracts.editButton')}
            </KoobButton>
          ) : (
            <>
              <KoobInputSelect
                label={t('bulkEdit.editor.visibility')}
                placeholder={t('bulkEdit.editor.noChange')}
                value={enabledValue}
                onValueChange={setEnabledValue}
                options={[
                  { label: t('bulkEdit.editor.noChange'), value: '' },
                  { label: t('bulkEdit.editor.visible'), value: true },
                  { label: t('bulkEdit.editor.invisible'), value: false },
                ]}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ContractList({ itemField, hotelId }) {
  const { t } = useTranslation('toConnectionRequest');

  const { data, isLoading } = useAsync({
    promiseFn: getHotelContracts,
    hotelId,
    filters: {
      status: HotelContractStatusEnum.PUBLISHED,
      contractStateFilter: ContractStateFilterEnum.AVAILABLE,
    },
  });

  const contracts = data?.data ?? [];

  return (
    <>
      {isLoading || contracts?.length > 0 ? (
        <div className="flex-col space-y-5">
          {isLoading && (
            <>
              <KoobPlaceholder className="h-20 w-full rounded-md" />
              <KoobPlaceholder className="h-20 w-full rounded-md" />
              <KoobPlaceholder className="h-20 w-full rounded-md" />
            </>
          )}

          {!isLoading &&
            contracts.map((contract) => (
              <ContractListItem
                key={contract.id}
                itemField={itemField}
                contract={contract}
              />
            ))}
        </div>
      ) : (
        <div className="h-60 flex flex-col justify-center items-center">
          <div className="text-center">
            <i className="far fa-file-signature text-4xl text-gray-300" />

            <div className="mt-3 font-medium text-gray-500">
              {t('bulkEdit.editor.contracts.noContracts')}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function BulkEditorItemContracts({
  itemField,
  hotelId,
  hotelName,
  disabled,
}) {
  const { t } = useTranslation('toConnectionRequest');

  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <KoobButton
        variant="primary"
        onClick={() => setShowModal(true)}
        size="sm"
        disabled={disabled}
      >
        <i className="fal fa-file-signature text-lg" />
      </KoobButton>

      <KoobModal open={showModal} setOpen={setShowModal}>
        <KoobTitle>{t('bulkEdit.editor.contracts.title')}</KoobTitle>
        <KoobParagraph>{hotelName}</KoobParagraph>

        <div className="mt-5">
          {showModal && (
            <ContractList itemField={itemField} hotelId={hotelId} />
          )}
        </div>
      </KoobModal>
    </>
  );
}
